import { useQuery } from '@tanstack/react-query';
import { findLast, isNumber, map } from 'lodash';

import { DeviceLatestData, SurfaceTemperatureData, WavesData, WindData } from '@/api/devices';
import { SPOTTER_SDK_TOKEN } from '@/api/oceansApi/constants';
import { RequestOptions, axios } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { APIResponse, LocationData, SpotterFamily } from '@/common/types';

type GetOceansLatestDataResponse = APIResponse<
    {
        spotterId: string;
        spotterName: string;
        payloadType: string;
        batteryVoltage: number;
        batteryPower: number;
        solarVoltage: number;
        humidity: number;
        track: LocationData[];
        waves: WavesData[];
        wind: WindData[];
        barometerData: ({ value: number } & LocationData)[];
        surfaceTemp: SurfaceTemperatureData[];
    }[]
>;

type GetOceansLatestDataRequest = {
    includeSharedDevices?: boolean;
    includeSurfaceTemp?: boolean;
    includeBarometer?: boolean;
    gimmeGridData?: boolean;
};

const isValidLocation = (datum: LocationData | { location: LocationData }) => {
    const { latitude, longitude } = 'location' in datum ? datum.location : datum;
    return isNumber(latitude) && isNumber(longitude);
};

const lastValidLocation = <D extends LocationData | { location: LocationData }>(data: D[]): D | undefined =>
    findLast(data, isValidLocation);

/**
 * Transforms result into something shaped vaguely like a subset of Device. This is intended to be compatible with @sofarocean/atlas's useSpottersByID,
 * since we want it to match what we see in its ConnectedMapSpotters layer, but jest doesn't like something about the build chain over there, so
 * we're replicating it here.
 */
export const getOceansLatestData = async (
    params: GetOceansLatestDataRequest,
    options?: RequestOptions
): Promise<APIResponse<DeviceLatestData[]>> => {
    const { data: rawData, status } = await axios.get<unknown, GetOceansLatestDataResponse>('/oceans-api/latest-data', {
        params,
        headers: { token: SPOTTER_SDK_TOKEN },
        ...options,
    });

    const data = map(rawData, ({ spotterName, waves, wind, barometerData, surfaceTemp, ...data }) => {
        const lastBarometerData = lastValidLocation(barometerData);
        return {
            ...data,
            name: spotterName,
            waves: lastValidLocation(waves),
            wind: lastValidLocation(wind),
            airPressure: lastBarometerData && { location: lastBarometerData, pressure: lastBarometerData.value },
            surfaceTemperature: lastValidLocation(surfaceTemp),
        };
    });

    return { data, status };
};

type UseOceansWaveDataOptions = {
    config?: QueryConfig<typeof getOceansLatestData>;
    spotterFamily?: SpotterFamily;
};

export const useOceansLatestData = ({ config, spotterFamily }: UseOceansWaveDataOptions) => {
    if (config?.enabled && spotterFamily === 'mine') {
        throw new Error('useOceansLatestData supports only shared and grid spotters');
    }
    const sharedSpotters = spotterFamily === 'shared';
    const gridSpotters = spotterFamily === 'grid';

    const params = {
        includeSharedDevices: sharedSpotters,
        includeSurfaceTemp: true,
        includeBarometer: true,
        gimmeGridData: gridSpotters ? gridSpotters : undefined, // don't expose the flag if not retrieving grid data
    };
    return useQuery({
        queryKey: ['oceans-latest-data', spotterFamily],
        queryFn: ({ signal }) => getOceansLatestData(params, { signal }),
        staleTime: 1000 * 60 * 5,
        ...config,
    });
};
